import type { PlayerInput } from "../shared/types";

export class KeyboardInput {
  private keys = new Set<string>();
  private queuedFire = false;
  private queuedHoming = false;
  private queuedMine = false;
  private queuedViewToggle = false;
  private seq = 0;

  constructor() {
    window.addEventListener("keydown", this.handleKeyDown);
    window.addEventListener("keyup", this.handleKeyUp);
    window.addEventListener("focusin", this.handleFocusIn);
    window.addEventListener("blur", this.clear);
  }

  destroy(): void {
    window.removeEventListener("keydown", this.handleKeyDown);
    window.removeEventListener("keyup", this.handleKeyUp);
    window.removeEventListener("focusin", this.handleFocusIn);
    window.removeEventListener("blur", this.clear);
  }

  sample(): PlayerInput {
    this.seq += 1;
    const fire = this.keys.has("Space") || this.queuedFire;
    const homing = this.queuedHoming;
    const mine = this.queuedMine;
    this.queuedFire = false;
    this.queuedHoming = false;
    this.queuedMine = false;

    return {
      seq: this.seq,
      clientTime: performance.now(),
      thrust: this.keys.has("KeyW") ? 1 : 0,
      turn: (this.keys.has("KeyA") ? 1 : 0) + (this.keys.has("KeyD") ? -1 : 0),
      vertical: (this.keys.has("KeyF") ? 1 : 0) + (this.keys.has("KeyS") ? -1 : 0),
      fire,
      homing,
      mine
    };
  }

  resumeAfter(sequence: number): void {
    this.clear();
    this.seq = Math.max(this.seq, sequence);
  }

  setControl(code: string, pressed: boolean): void {
    if (!isGameplayKey(code)) return;
    if (pressed) {
      this.keys.add(code);
      if (code === "Space") this.queuedFire = true;
    }
    else this.keys.delete(code);
  }

  reset(): void {
    this.clear();
  }

  consumeViewToggle(): boolean {
    const toggle = this.queuedViewToggle;
    this.queuedViewToggle = false;
    return toggle;
  }

  private handleKeyDown = (event: KeyboardEvent): void => {
    if (isTextEntryEvent(event)) return;

    if (isGameplayKey(event.code)) {
      event.preventDefault();
      if (!event.repeat) {
        if (event.code === "Space") this.queuedFire = true;
        if (event.code === "KeyH") this.queuedHoming = true;
        if (event.code === "KeyM") this.queuedMine = true;
        if (event.code === "KeyP") this.queuedViewToggle = true;
      }
      this.keys.add(event.code);
    }
  };

  private handleKeyUp = (event: KeyboardEvent): void => {
    if (isGameplayKey(event.code)) {
      if (isTextEntryEvent(event)) {
        this.keys.delete(event.code);
        return;
      }
      event.preventDefault();
      this.keys.delete(event.code);
    }
  };

  private handleFocusIn = (event: Event): void => {
    if (eventPathIncludesTextEntryTarget(event)) this.clear();
  };

  private clear = (): void => {
    this.keys.clear();
    this.queuedFire = false;
    this.queuedHoming = false;
    this.queuedMine = false;
    this.queuedViewToggle = false;
  };
}

function isGameplayKey(code: string): boolean {
  return ["KeyW", "KeyA", "KeyD", "KeyF", "KeyS", "Space", "KeyH", "KeyM", "KeyP"].includes(code);
}

function isTextEntryEvent(event: KeyboardEvent): boolean {
  return eventPathIncludesTextEntryTarget(event);
}

function eventPathIncludesTextEntryTarget(event: Event): boolean {
  const path = typeof event.composedPath === "function" ? event.composedPath() : [];
  const targets = path.length ? path : [event.target];
  return targets.some(isTextEntryTarget);
}

function isTextEntryTarget(target: EventTarget | null | undefined): boolean {
  if (!target || typeof target !== "object") return false;

  const element = target as {
    getAttribute?: (name: string) => string | null;
    isContentEditable?: boolean;
    nodeName?: string;
    tagName?: string;
  };
  const tagName = (element.tagName ?? element.nodeName ?? "").toUpperCase();

  if (tagName === "INPUT" || tagName === "TEXTAREA" || tagName === "SELECT") return true;
  if (element.isContentEditable) return true;

  if (typeof element.getAttribute === "function") {
    const role = element.getAttribute("role")?.toLowerCase();
    const contentEditable = element.getAttribute("contenteditable")?.toLowerCase();
    return role === "textbox" || (Boolean(contentEditable) && contentEditable !== "false");
  }

  return false;
}
